package com.iregained.site.components.sections.clinicians.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.clinicians.styles.*
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// UNIQUE ANIMATION: Staggered Slide Left
@Composable
private fun Modifier.staggeredSlideLeft(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else 40.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun CliniciansFaqSection(data: CliniciansFaqData = defaultCliniciansFaqData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinicians-faq-section"

    var expandedIndex by remember { mutableStateOf<Int?>(null) }
    var isVisible by remember { mutableStateOf(false) }

    if (data.sectionTitle.isNotEmpty()) {
        Column(
            modifier = CliniciansFaqContainerStyle.toModifier().id(targetId).fillMaxWidth().background(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionTitle.isNotBlank()) SectionHeader(
                        text = data.sectionTitle,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 48.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s)),
                        alignment = Alignment.CenterHorizontally
                    )

                    Column(modifier = CliniciansFaqWrapperStyle.toModifier()) {
                        data.questions.forEachIndexed { index, faq ->
                            key(faq.question) {
                                if (faq.question.isNotBlank() && faq.answer.isNotBlank()) {
                                    val isExpanded = expandedIndex == index
                                    Column(
                                        modifier = CliniciansFaqItemStyle.toModifier()
                                            .staggeredSlideLeft(isVisible, 150 + (index * 150))
                                    ) {
                                        Row(
                                            modifier = CliniciansFaqHeaderStyle.toModifier()
                                                .onClick { expandedIndex = if (isExpanded) null else index },
                                            horizontalArrangement = Arrangement.SpaceBetween,
                                            verticalAlignment = Alignment.CenterVertically
                                        ) {
                                            Div(
                                                attrs = CliniciansFaqQuestionTextStyle.toModifier()
                                                    .fontWeight(if (isExpanded) FontWeight.Bold else FontWeight.SemiBold)
                                                    .color(if (isExpanded) palette.brand.primary else palette.custom.darkText)
                                                    .toAttrs {
                                                        ref { htmlElement ->
                                                            htmlElement.innerHTML = faq.question
                                                            onDispose {}
                                                        }
                                                    })
                                            SpanText(
                                                text = if (isExpanded) "−" else "+",
                                                modifier = CliniciansFaqToggleIconStyle.toModifier()
                                            )
                                        }
                                        if (isExpanded) {
                                            Box(modifier = CliniciansFaqAnswerBoxStyle.toModifier()) {
                                                Div(
                                                    attrs = CliniciansFaqAnswerTextStyle.toModifier().toAttrs {
                                                        ref { htmlElement ->
                                                            htmlElement.innerHTML = faq.answer; onDispose {}
                                                        }
                                                    })
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

// ==========================================
// CMS DATA MODEL: FAQ
// ==========================================
data class FaqItemData(
    val question: String, val answer: String
)

data class CliniciansFaqData(
    val sectionId: String? = null,
    val sectionTitle: String = "Frequently Asked Questions",
    val questions: List<FaqItemData> = listOf(
        FaqItemData(
            question = "What happens if the physical hardware requires maintenance?",
            answer = "The MyHand<sup>®</sup> System is designed to last, built with durable, medical-grade mechatronics suitable for high-volume clinic use. We also include periodic annual inspection in the maintenance package. In the rare event of a hardware failure, our White-Glove Support team provides rapid component replacement or even a loaner device if required, to ensure zero disruption to your workflow and patient schedule."
        ), FaqItemData(
            question = "What is the disinfection protocol between patients?",
            answer = "The MyHand<sup>®</sup> System is super easy and quick to clean and disinfect between patients as it features wipeable, medical-grade surfaces. The finger and thumb cups are designed for rapid sanitization using standard clinic-approved antimicrobial wipes, ensuring less than 2 minutes of turnover time between sessions."
        ), FaqItemData(
            question = "Does the software integrate with our EMR/EHR system?",
            answer = "Our cloud-based dashboard allows clinicians to easily export objective patient data (such as grip strength metrics and ROM improvements) into standard PDF or CSV formats for seamless attachment to patient charts in any standard EMR system."
        ), FaqItemData(
            question = "What are the footprint and spatial requirements?",
            answer = "The system is highly portable, mobile, battery operated and requires minimal floor space allowing it to be easily transported into different patient settings or locations within the clinic/hospital.\nMaximum System Dimensions (L x W x H) are: 80 cm x 120 cm x 170 cm (~32” x 47” x 67”)"
        ),
//        FaqItemData(
//            question = "How does the \$999/month promotional lease work at the end of the 36-month term?",
//            answer = "At the end of your 36-month promotional lease term, your facility has the flexible option to execute a simple buyout to retain permanent ownership of the asset, transition to a month-to-month agreement, or upgrade your hardware suite to our latest-generation mechatronics technology."
//        ),
        FaqItemData(
            question = "Are there potential tax optimizations associated with commercial leasing?",
            answer = "Yes. In many jurisdictions, operational leasing expenses can be deducted as direct operational overhead from corporate income. Additionally, qualifying facilities may utilize specialized asset depreciation deductions such as Section 179 in the US, section 35AD of Income Tax Act in India or appropriate Canadian capital cost allowances. We advise consulting your facility's corporate accounting team to confirm maximum deductions."
        ), FaqItemData(
            question = "What documentation is required to secure 24-hour leasing pre-approval?",
            answer = "The digital approval process administered by our financial partner is streamlined for efficiency. Standard applications typically require basic professional registry details, corporate bank routing summaries, and personal verification from practice leadership. Approvals are generally issued within 3-4 business day."
        ), FaqItemData(
            question = "Does device coverage and software integration continue during a lease term?",
            answer = "Absolutely. All devices procured under commercial leasing agreements maintain continuous access to comprehensive manufacturer hardware warranties, automated over-the-air digital software suites, and priority technical support coverage throughout the active lease term."
        )
    )
)

val defaultCliniciansFaqData = CliniciansFaqData()
